import React, { useEffect, useRef } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { Form, Input, Select, Row, Col, Divider } from "antd";
// import { CALL_API_UPDATED } from "../../../common/API";
import { CALL_GET_API } from "../../../../common/API";
import { useSelector } from "react-redux";
import { getCountryList } from "redux/selectors";

const { Option } = Select;

const Step1BasicInfo = ({ clientIdCount, isEditMode }) => {
    const {
        control,
        setValue,
        formState: { errors },
    } = useFormContext();

    const managingOffice = useWatch({
        control,
        name: "managingOffice", // ✅ Country of Managing Office
    });

    const countryList = useSelector(getCountryList);

    const lastGeneratedRef = useRef(""); // 🔒 prevents unnecessary setValue

    useEffect(() => {
        if (!managingOffice) return;

        // 🚫 In edit mode, don't touch clientId — keep the existing one
        if (isEditMode) return;

        const prefix = countryList.find(
            (c) => c?.countryname === managingOffice
        )?.countrycode;
        if (!prefix) return;

        const year = new Date().getFullYear();
        const nextNumber = String((clientIdCount?.count ?? 0) + 1).padStart(3, "0");

        const generatedClientId = `${prefix}-${year}-00${nextNumber}`;

        // 🚀 Prevent re-setting same value (BIG speed win)
        if (lastGeneratedRef.current === generatedClientId) return;

        lastGeneratedRef.current = generatedClientId;

        setValue("clientId", generatedClientId, {
            shouldDirty: true,
            shouldTouch: false,
            shouldValidate: false, // ⚡ NO validation = FAST
        });
    }, [managingOffice, clientIdCount?.count, isEditMode, setValue, countryList]);

    const IncorporationData = [
        "Sole Proprietorship",
        "General Partnership",
        "Local Limited Liab Company",
        "Listed Entity",
        "Branch of Regional/MNC",
        "Offshore Holding Company",
        "Non-profit Organisation",
        "Paratsatal/Government Department",
        "Other - Corporation",
        "Co - operation",
        "Local Ltd Liability Co.",
        "Creadit Union",
        "Fund",
        "Government Organisation",
        "Induvidual",
        "Limited Partnership",
        "Private Corporation",
        "Public Corporation",
        "Trust",
    ];

    return (
        <div style={{ background: "#fff", padding: 25, borderRadius: 8 }}>
            <h5 style={{ marginBottom: 20, fontWeight: 600 }}>Company Information</h5>

            <Form layout="vertical">
                <Row gutter={24}>
                    <Col span={12}>


                        {/* -------- Client ID -------- */}
                        <Form.Item
                            label={<span><span style={{ color: "red" }}>*</span> Client ID</span>}
                            validateStatus={errors.clientId ? "error" : ""}
                            help={errors.clientId?.message}
                        >
                            <Controller
                                name="clientId"
                                control={control}
                                rules={{ required: "Client ID is required" }}
                                render={({ field }) => (
                                    <Input {...field} disabled placeholder="Auto Generated" />
                                )}
                            />
                        </Form.Item>


                        {/* -------- Relationship Type -------- */}
                        <Form.Item
                            label={<span><span style={{ color: "red" }}>*</span> Relationship type</span>}
                            validateStatus={errors.relationshipType ? "error" : ""}
                            help={errors.relationshipType?.message}
                        >
                            <Controller
                                name="relationshipType"
                                control={control}
                                rules={{ required: "Relationship type is required" }}
                                render={({ field }) => <Input {...field} />}
                            />
                        </Form.Item>


                        <Form.Item
                            label={<span><span style={{ color: "red" }}>*</span> Company’s Registered Name</span>}
                            validateStatus={errors.companyName ? "error" : ""}
                            help={errors.companyName?.message}
                        >
                            <Controller
                                name="companyName"
                                control={control}
                                rules={{ required: "Company name is required" }}
                                render={({ field }) => <Input {...field} />}
                            />
                        </Form.Item>

                        <Form.Item label={<span> Size</span>}>
                            <Controller
                                name="size"
                                control={control}
                                render={({ field }) => <Input placeholder="Enter" {...field} />}
                            />
                        </Form.Item>

                        {/* -------- Managing Office -------- */}
                        <Form.Item
                            label="Country of Managing Office"
                            required
                            validateStatus={errors.managingOffice ? "error" : ""}
                            help={errors.managingOffice?.message}
                        >
                            <Controller
                                name="managingOffice"
                                control={control}
                                rules={{
                                    required: "Managing office is required",
                                }}
                                render={({ field }) => (
                                    <Select
                                        placeholder="Select"
                                        {...field}
                                        onChange={(value) => field.onChange(value)}
                                    >
                                        {countryList?.map((country) => (
                                            <Select.Option key={country.countrycode} value={country.countryname}>
                                                {country.countryname}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </Form.Item>



                        <Form.Item label={<span>Incorporation Type</span>}>
                            <Controller
                                name="incorporationType"
                                control={control}
                                render={({ field }) => (
                                    <Select placeholder="Select" {...field}>
                                        {IncorporationData?.map((country, index) => (
                                            <Option key={index} value={country}>
                                                {country}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </Form.Item>

                        <Form.Item label={<span>Primary Industry</span>}>
                            <Controller
                                name="primaryIndustry"
                                control={control}
                                render={({ field }) => <Input placeholder="Enter" {...field} />}
                            />
                        </Form.Item>
                        <Form.Item
                            label={<span> <span style={{ color: "red" }}>*</span> Primary Contact Person</span>}
                            validateStatus={errors.primaryContactPerson ? "error" : ""}
                            help={errors.primaryContactPerson?.message}
                        >
                            <Controller
                                name="primaryContactPerson"
                                control={control}
                                rules={{
                                    required: "Primary contact person is required",
                                    pattern: {
                                        value: /^[a-zA-Z\s]+$/,
                                        message: "Only alphabets are allowed",
                                    },
                                    minLength: {
                                        value: 3,
                                        message: "Name must be at least 3 characters",
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Enter primary contact person"
                                    />
                                )}
                            />
                        </Form.Item>




                        {/* -------- Description -------- */}
                        <Form.Item label="Description">
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <Input.TextArea
                                        rows={4}
                                        placeholder="Enter description"
                                        {...field}
                                    />
                                )}
                            />
                        </Form.Item>

                    </Col>

                    {/* ---------------- SECOND COLUMN ---------------- */}
                    <Col span={12}>
                        <Form.Item
                            label={<span><span style={{ color: "red" }}>*</span> Influential / Star Customer</span>}
                            validateStatus={errors.influential ? "error" : ""}
                            help={errors.influential?.message}
                        >
                            <Controller
                                name="influential"
                                control={control}
                                rules={{ required: "Please select Yes or No" }}
                                render={({ field }) => (
                                    <Select placeholder="Select" {...field}>
                                        <Option value="true">Yes</Option>
                                        <Option value="false">No</Option>
                                    </Select>
                                )}
                            />
                        </Form.Item>


                        <Form.Item
                            label={<span><span style={{ color: "red" }}>*</span> Current Nature of Engagement</span>}
                            validateStatus={errors.engagement ? "error" : ""}
                            help={errors.engagement?.message}
                        >
                            <Controller
                                name="engagement"
                                control={control}
                                rules={{ required: "Current nature of engagement is required" }}
                                render={({ field }) => <Input {...field} />}
                            />
                        </Form.Item>


                        <Form.Item label="Owner">
                            <Controller
                                name="owner"
                                control={control}
                                render={({ field }) => <Input placeholder="Enter" {...field} />}
                            />
                        </Form.Item>

                        {/* <Form.Item label={<span><span style={{ color: "red" }}></span> Country of incorporation</span>}>
                            <Controller
                                name="countryIncorp"
                                control={control}
                                render={({ field }) =>
                                    <Select placeholder="Select" {...field}>
                                        {ManagingOffice?.map((country, index) => (
                                            <Option key={index} value={country}>
                                                {country}
                                            </Option>
                                        ))}
                                    </Select>
                                }
                            />
                        </Form.Item> */}



                        <Form.Item
                            label={
                                <span>
                                    <span style={{ color: "red" }}>*</span> Office Email
                                </span>
                            }
                            validateStatus={errors.officeEmail ? "error" : ""}
                            help={errors.officeEmail?.message}
                        >
                            <Controller
                                name="officeEmail"
                                control={control}
                                rules={{
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: "Enter a valid email address",
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        onChange={(e) =>
                                            field.onChange(e.target.value.toLowerCase())
                                        }
                                    />
                                )}
                            />
                        </Form.Item>



                        <Form.Item label="Company Website URL">
                            <Controller
                                name="websiteURL"
                                control={control}
                                render={({ field }) => <Input placeholder="Enter" {...field} />}
                            />
                        </Form.Item>

                        <Form.Item label="Primary Sector">
                            <Controller
                                name="primarySector"
                                control={control}
                                render={({ field }) => <Input placeholder="Enter" {...field} />}
                            />
                        </Form.Item>
                        <Form.Item
                            label={<span> <span style={{ color: "red" }}>*</span> Primary Contact Email</span>}
                            validateStatus={errors.primaryContactEmail ? "error" : ""}
                            help={errors.primaryContactEmail?.message}
                        >
                            <Controller
                                name="primaryContactEmail"
                                control={control}
                                rules={{
                                    required: "Email is required",
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: "Enter a valid email address",
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Enter primary contact email"
                                        onChange={(e) =>
                                            field.onChange(e.target.value.toLowerCase())
                                        }
                                    />
                                )}
                            />
                        </Form.Item>


                    </Col>
                </Row>

                <Divider style={{ margin: "30px 0 20px 0" }} />
                <h5 style={{ marginBottom: 20, fontWeight: 600 }}>Address</h5>

                <Row gutter={24}>
                    {/* -------- LEFT COLUMN -------- */}
                    <Col span={12}>
                        <Form.Item label="Address Type">
                            <Controller
                                name="addressType"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Registered Address">
                            <Controller
                                name="registeredAddress"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Country">
                            <Controller
                                name="country"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Registered No">
                            <Controller
                                name="registeredNo"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Registered Office Postal Address">
                            <Controller
                                name="registeredOfficePostalAddress"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    {/* -------- RIGHT COLUMN -------- */}
                    <Col span={12}>
                        <Form.Item label="Use Group Address">
                            <Controller
                                name="useGroupAddress"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Address 1 : City">
                            <Controller
                                name="city"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Operating Countries">
                            <Controller
                                name="operatingCountries"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Pin No">
                            <Controller
                                name="pinNo"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>

                        <Form.Item label="Postal Code">
                            <Controller
                                name="postalCode"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter" {...field} />
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>

            </Form>
        </div>
    );
};

export default Step1BasicInfo;